import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
// @ts-ignore
import { migrateDiagram } from '@bpmn-io/dmn-migrate/dist';
// @ts-ignore
import DmnModeler from 'dmn-js/dist/dmn-modeler.production.min.js';
// @ts-ignore
import DmnViewer from 'dmn-js/dist/dmn-viewer.production.min.js';
import 'dmn-js/dist/assets/diagram-js.css';
import 'dmn-js/dist/assets/dmn-font/css/dmn-embedded.css';
import 'dmn-js/dist/assets/dmn-js-decision-table-controls.css';
import 'dmn-js/dist/assets/dmn-js-decision-table.css';
import 'dmn-js/dist/assets/dmn-js-drd.css';
import 'dmn-js/dist/assets/dmn-js-literal-expression.css';
import 'dmn-js/dist/assets/dmn-js-shared.css';

/* Details on DMN findings can be found in below wiki page

https://hello.atlassian.net/wiki/spaces/COMITFO/pages/1588893797/Findings+of+Custom+DMN+UI+Integration+with+Approval+Desk
*/

interface TableModeProps {
  xml: string;
  setDmnLibraryError: (_error: any) => void;
  id: string;
  editMode: boolean;
}

//disabling features which are not required
const additionalModules = [
  {
    viewDrd: ['value', null],
  },
  {
    poweredBy: ['value', null],
  },
  {
    hitPolicyProvider: ['value', null],
  },
  {
    description: ['value', null],
  },
  {
    expressionLanguage: ['value', null],
  },
  {
    columnResizeProvider: ['value', null],
  },
  {
    inputEditingProvider: ['value', null],
  },
  {
    outputEditingProvider: ['value', null],
  },
  {
    decisionTableProperties: ['value', null],
  },
];

const TableMode = forwardRef(({ xml, setDmnLibraryError, id, editMode }: TableModeProps, ref) => {
  const [dmnContainer, setDmnContainer] = useState<HTMLDivElement | null>(null);

  const modeler = useRef(
    new DmnModeler({
      decisionTable: { additionalModules },
      canvas: {
        deferUpdate: false,
      },
    })
  );

  const viewer = useRef(
    new DmnViewer({
      decisionTable: { additionalModules },
      canvas: {
        deferUpdate: false,
      },
    })
  );

  const createModelerDiagram = useCallback(
    async (xml: string) => {
      try {
        xml = await migrateDiagram(xml);
        await modeler.current.importXML(xml);
        const views = modeler.current.getViews();
        const decisionTableViews = views.filter(({ type }: { type: string }) => type === 'decisionTable');
        if (decisionTableViews.length) {
          modeler.current.open(decisionTableViews[0]);
        }
      } catch (err) {
        setDmnLibraryError(err as Error);
      }
    },
    [xml]
  );

  const createViewerDiagram = useCallback(
    async (xml: string) => {
      try {
        xml = await migrateDiagram(xml);
        await viewer.current.importXML(xml);
        const views = viewer.current.getViews();
        const decisionTableViews = views.filter(({ type }: { type: string }) => type === 'decisionTable');
        if (decisionTableViews.length) {
          viewer.current.open(decisionTableViews[0]);
        }
      } catch (err) {
        setDmnLibraryError(err as Error);
      }
    },
    [xml]
  );

  useEffect(() => {
    // Not able to select any dropdown options: when you select any option dropdown gets closed without selecting that option. dmn library doesn't work properly when you place under any focusible element, so removing tabindex from root element and putting it back when component unmounts
    let appLayoutContainerTabindexValue = '';
    const container = document.querySelector('div[data-layout-container]');
    if (container) {
      const attr = container.getAttribute('tabindex');
      if (attr) {
        appLayoutContainerTabindexValue = attr;
        container.removeAttribute('tabindex');
      }
    }
    return () => {
      if (container && appLayoutContainerTabindexValue) {
        container.setAttribute('tabindex', appLayoutContainerTabindexValue);
      }
    };
  }, []);

  useEffect(() => {
    if (xml && dmnContainer) {
      if (editMode) {
        viewer.current.detach(dmnContainer);
        createModelerDiagram(xml);
        modeler.current.attachTo(dmnContainer);
      } else {
        modeler.current.detach(dmnContainer);
        createViewerDiagram(xml);
        viewer.current.attachTo(dmnContainer);
      }
    }
  }, [createModelerDiagram, createViewerDiagram, xml, editMode, dmnContainer, id]);

  useImperativeHandle(
    ref,
    () => ({
      getXml: async () => (await modeler.current.saveXML({ format: true })).xml,
    }),
    []
  );

  return <div ref={setDmnContainer} id={id} className="dmn-container" data-testid="dmn-container" />;
});
TableMode.displayName = 'TableMode';

export default TableMode;
